import { useEffect, useMemo, useState } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../lib/session'
import { useRoster } from '../lib/roster'
import { canSend, localDate, problemsWith, resolveAudience } from '../lib/campaigns'
import { queueReviewed } from '../lib/outbox'
import { formatDateLong, fullName } from '../lib/format'
import { isGuidanceTemplate, type EmailTemplate } from '../lib/types'
import {
  daysBetween,
  dripTrigger,
  dueTouches,
  ENROLMENT_LABEL,
  enrolDescription,
  isCalendarDate,
  isComplete,
  isListeningForNewAgents,
  loadDrips,
  NEW_AGENT_TRIGGER,
  nextStep,
  progressFor,
  resumeStart,
  scheduleFor,
  stepOccurrence,
  type DripBundle,
  type DripCampaign,
  type DripEnrolment,
  type DripEnrolmentMode,
  type DripLane,
  type DripTouch,
} from '../lib/drips'

/**
 * Drip campaigns — a named sequence of touches, each a fixed number of days
 * after the one before it.
 *
 * NOTHING ON THIS SCREEN SENDS AN EMAIL. Approving a due touch writes drafts
 * that wait for Renee. That is the same line the rest of the application is
 * built on, and it matters more here than anywhere else: this is the only
 * feature that decides *by itself* that somebody should be emailed today.
 *
 * THEY WAIT IN THE **OUTBOX**, NOT IN OUTREACH.
 *
 * Both tabs show them — Outreach lists every non-manual trigger, so drip cards
 * appear there beside the birthdays — but Outreach is explicitly review-only
 * and says so in a banner: "Nothing here can be sent or marked sent, on
 * purpose". The Outbox is where a card has a working Send button. Every piece
 * of copy on this screen therefore names the Outbox, and getting that wrong
 * makes the whole feature look broken on first use: approve forty, follow the
 * message to Outreach, and read that nothing there can be sent.
 *
 * WHAT IS DUE IS WORKED OUT WHEN SHE OPENS THE TAB.
 *
 * There is no scheduled job anywhere in this system — no cron, no worker. So
 * "touch 2 is due today" is a calculation, not an event, and the calculation
 * runs in the browser. Two consequences worth knowing rather than discovering:
 * a touch that came due while nobody was looking is still waiting when somebody
 * looks, and nothing happens on a day she does not open the app. Both are
 * better than the alternative, which is a machine emailing agents unattended.
 *
 * Because a due touch is worked out rather than recorded, this screen may
 * compute the same batch twice. It cannot send it twice: the drafts are keyed
 * `(agent_id, 'drip:<id>', 's<step>')` against a unique constraint, so the
 * second write collides and reports itself.
 */

const LANE_LABEL: Record<DripLane, string> = {
  reviewed: 'Read and send each one',
  blast: 'Same message to everybody',
}


export default function Drips() {
  const { appUser } = useAuth()
  const { agents, tagsByAgent, tagsLoaded, tagsError, selection, clearSelection } = useRoster()

  const [bundle, setBundle] = useState<DripBundle | null>(null)
  const [templates, setTemplates] = useState<EmailTemplate[]>([])
  const [busy, setBusy] = useState<string | null>(null)
  const [note, setNote] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [open, setOpen] = useState<string | null>(null)
  const [newName, setNewName] = useState('')
  const [newLane, setNewLane] = useState<DripLane>('reviewed')
  const [newEnrolment, setNewEnrolment] = useState<DripEnrolmentMode>('manual')
  /**
   * The listening date she has CHOSEN for the campaign being created, or null
   * for "hasn't chosen, use today".
   *
   * Null rather than a date seeded once at mount, and that is the whole point:
   * this app is left open all day and pinned across days. A `useState(() =>
   * today)` initialiser runs once, so a tab opened three weeks ago would offer
   * a three-week-old date as its default — silently, in the past, which is the
   * direction that hurts.
   */
  const [newListensFrom, setNewListensFrom] = useState<string | null>(null)
  /**
   * Unsaved edits to a live campaign's listening date, by campaign id. Cleared
   * by every reload — see `load`.
   */
  const [listensDraft, setListensDraft] = useState<Record<string, string>>({})
  /** Which campaign is asking "are you sure" about being deleted. */
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null)
  /** Which enrolment is asking "are you sure" about being stopped. */
  const [confirmStop, setConfirmStop] = useState<number | null>(null)
  /**
   * Which campaign the last message belongs to.
   *
   * A single note at the top of the page is fine when there is one campaign and
   * useless when there are several: "1 person added" floats above a list of
   * cards that all look alike, and the one thing worth knowing — WHICH — is the
   * part easiest to miss. Pinning the message to its own campaign is how adding
   * somebody to the wrong one becomes visible instead of silent.
   */
  const [noteFor, setNoteFor] = useState<string | null>(null)

  const today = localDate(new Date())
  /**
   * The date the new campaign would listen from — her choice, or today.
   *
   * `||` and not `??`. Clearing the date box reports an empty string, not null,
   * and `??` would let that through: Create then greys out with nothing on
   * screen explaining why, on a form she has otherwise filled in correctly.
   */
  const newListens = newListensFrom || today

  const load = async () => {
    const b = await loadDrips()
    setBundle(b)
    /**
     * An armed confirm does not survive a reload.
     *
     * `confirmStop` and `confirmDelete` used to persist through everything —
     * closing and reopening a campaign, any other action, and any change made
     * in the other window. Two people use this app. Arm a stop, have the other
     * one act on that same person, press "Put back in" to undo them, and the
     * row came back with "Yes, stop them" already showing: one click from
     * undoing your own undo, on a prompt you asked for ten minutes earlier.
     */
    setConfirmStop(null)
    setConfirmDelete(null)
    /**
     * An unsaved date edit DOES survive a reload — only the campaigns that have
     * gone are dropped.
     *
     * This is deliberately the opposite of the confirms above, and the two are
     * not analogous. Clearing an armed confirm removes a hazard; clearing this
     * destroys a deliberate choice she has already read on screen. And this
     * screen reloads after every action, including "Add a touch", which is
     * directly below the date box and is exactly what somebody does between
     * setting a date and saving it.
     */
    setListensDraft((d) => {
      const live = new Set(b.campaigns.map((c) => c.id))
      const next: Record<string, string> = {}
      for (const [id, value] of Object.entries(d)) if (live.has(id)) next[id] = value
      return next
    })
    /**
     * A message whose campaign has gone falls back to the top of the page
     * rather than vanishing. Approving forty emails and then reporting nothing
     * at all is the bad version of pinning messages to cards.
     */
    setNoteFor((id) => (id && b.campaigns.some((c) => c.id === id) ? id : null))
  }

  useEffect(() => {
    void load()
    supabase
      .from('email_templates')
      .select('*')
      .order('name')
      .then(({ data }) => setTemplates((data ?? []) as EmailTemplate[]))
  }, [])

  const byId = useMemo(() => {
    const m = new Map<string, { first_name: string | null; last_name: string | null; rezen_id: string }>()
    for (const a of agents ?? []) m.set(a.rezen_id, a)
    return m
  }, [agents])

  /**
   * The roster and the tags are part of "is this screen ready", not a detail to
   * fill in later.
   *
   * The three drip tables are small and answer fast; the 785-row paged roster
   * does not. Reloading on /drips in between meant `picked` was empty, every
   * audience resolved to nobody, and pressing Approve on a batch of 40 said
   * "nobody in that batch can be emailed — they have no address, have left, or
   * are tagged out." A false and alarming sentence, with nothing suggesting
   * that waiting two seconds would fix it. The composer already gets this
   * right; this is the same guard.
   */
  if (!bundle || !agents || !tagsLoaded) return <div className="empty">Loading…</div>
  if (bundle.error) {
    return (
      <div className="board tpl">
        <section className="sec">
          <p className="error">{bundle.error}</p>
          <button className="btn" onClick={() => void load()}>Try again</button>
        </section>
      </div>
    )
  }

  const touchesOf = (id: string) =>
    bundle.touches.filter((t) => t.campaign_id === id).sort((a, b) => a.step - b.step)
  const enrolmentsOf = (id: string) => bundle.enrolments.filter((e) => e.campaign_id === id)
  const sentSteps = (campaignId: string, agentId: string) => {
    const out = new Set<number>()
    for (const key of bundle.sent) {
      const [c, a, s] = key.split(' ')
      if (c === campaignId && a === agentId) out.add(Number(s))
    }
    return out
  }

  /**
   * The `${agentId} ${step}` set one campaign's scheduler needs, sliced out of
   * the whole-database set the loader built.
   */
  const sentFor = (campaignId: string) => {
    const out = new Set<string>()
    for (const key of bundle.sent) {
      const [c, a, s] = key.split(' ')
      if (c === campaignId) out.add(`${a} ${s}`)
    }
    return out
  }

  async function createCampaign() {
    const name = newName.trim()
    if (!name) return
    const listening = newEnrolment === 'trigger'
    /**
     * Refused here rather than by the database.
     *
     * `drip_listens_from_matches_enrolment` already refuses this, but what
     * comes back is the constraint's name, and a constraint name on screen is
     * indistinguishable from the app being broken.
     */
    if (listening && !isCalendarDate(newListens)) {
      setNote(null)
      setNoteFor(null)
      return setError(
        'That start date is not a day on the calendar. Pick the date the campaign should start listening from.',
      )
    }
    setBusy('new')
    setError(null)
    const { error } = await supabase.from('drip_campaigns').insert({
      name,
      lane: newLane,
      enrolment: newEnrolment,
      /**
       * Both of these are null on a manual campaign, and that is not tidiness:
       * `drip_trigger_key_matches_enrolment` and
       * `drip_listens_from_matches_enrolment` each refuse the row outright if
       * one is set without the other.
       */
      trigger_key: listening ? NEW_AGENT_TRIGGER : null,
      listens_from: listening ? newListens : null,
      status: 'active',
      created_by: appUser?.id ?? null,
    })
    setBusy(null)
    if (error) return setError(error.message)
    setNewName('')
    /**
     * The pickers go back to their defaults too, not just the name.
     *
     * Leaving them where they were means the next campaign she types is created
     * as a second listening campaign without her choosing it — and two of those
     * put every new agent into both sequences.
     */
    setNewEnrolment('manual')
    setNewListensFrom(null)
    setNoteFor(null)
    setNote(
      listening
        ? `"${name}" created. New agents who join on or after ${formatDateLong(newListens)} will be added to it on their own. Write its touches now — an agent added before there is anything to send just sits there, and gets the whole sequence at once when there finally is.`
        : `"${name}" created. Add its touches, then put people in it.`,
    )
    await load()
  }

  /**
   * Change the date a live trigger campaign listens from.
   *
   * Editable because the alternative is a dead end: get the date wrong and the
   * only way out is End it, then Delete it, which is two irreversible-looking
   * buttons to fix a typo.
   *
   * It is not retroactive, and the screen says so. The daily run only considers
   * agents it has just created, so moving the date backwards does not reach
   * into the roster and enrol anybody who is already on it.
   */
  async function saveListensFrom(campaign: DripCampaign, value: string) {
    if (!isCalendarDate(value)) {
      setNote(null)
      setNoteFor(null)
      return setError(
        'That start date is not a day on the calendar. Pick the date the campaign should start listening from.',
      )
    }
    if (value === campaign.listens_from) return
    setBusy(`listens-${campaign.id}`)
    setError(null)
    /**
     * `select` and a row count, like every other write on this screen. Without
     * it a campaign deleted in the other window comes back as a success with no
     * error and no rows, and the screen says the date was saved.
     */
    const { data, error } = await supabase
      .from('drip_campaigns')
      .update({ listens_from: value })
      .eq('id', campaign.id)
      .select('id')
    setBusy(null)
    if (error) return setError(error.message)
    if (!data || data.length === 0) {
      setError(
        `"${campaign.name}" was not there to change — it may have been deleted in another window.`,
      )
      return void load()
    }
    setListensDraft((d) => {
      const next = { ...d }
      delete next[campaign.id]
      return next
    })
    setNoteFor(campaign.id)
    setNote(
      campaign.status === 'active'
        ? `Now listening for agents who join on or after ${formatDateLong(value)}.`
        : `Saved. It will pick up agents who join on or after ${formatDateLong(value)} — but not while this campaign is ${campaign.status}.`,
    )
    await load()
  }

  /** Emails of this campaign still unsent in the Outbox. */
  const pendingFor = (campaignId: string) => bundle.pending.get(campaignId) ?? 0

  /** Every step this campaign has ever written a draft for, live or deleted. */
  function everSent(campaignId: string): Set<number> {
    const out = new Set<number>()
    for (const key of bundle!.sent) {
      const [c, , s] = key.split(' ')
      if (c === campaignId) out.add(Number(s))
    }
    return out
  }

  async function addTouch(campaign: DripCampaign) {
    const existing = touchesOf(campaign.id)
    setBusy(`touch-${campaign.id}`)
    setError(null)
    const { error } = await supabase.from('drip_touches').insert({
      campaign_id: campaign.id,
      step: nextStep(existing, everSent(campaign.id)),
      // Nought for the first, a month for the rest — a starting point she can
      // change, not a decision made for her.
      delay_days: existing.length === 0 ? 0 : 30,
      subject: '',
      body: '',
    })
    setBusy(null)
    if (error) return setError(error.message)
    await load()
  }

  async function saveTouch(t: DripTouch, patch: Partial<DripTouch>) {
    setBusy(`save-${t.id}`)
    setError(null)
    const { data, error } = await supabase
      .from('drip_touches')
      .update(patch)
      .eq('id', t.id)
      .select('id')
    setBusy(null)
    if (error) return setError(error.message)
    // A zero-row update is a success with no error attached. Without this the
    // screen would say saved for a touch that no longer exists.
    if (!Array.isArray(data) || data.length === 0) {
      return setError('That touch is no longer there — it may have been deleted in another window. Reload.')
    }
    /**
     * Patched in place rather than reloaded.
     *
     * `load()` re-reads four tables and rebuilds the whole page. Doing that
     * after every saved field made a reload land underneath whatever she was
     * typing next, and reading the drafts table on each one made it slow enough
     * to notice. Nothing else on screen depends on a touch's wording.
     */
    setBundle((b) =>
      b ? { ...b, touches: b.touches.map((x) => (x.id === t.id ? { ...x, ...patch } : x)) } : b,
    )
  }

  /**
   * Deleting a touch that has already gone out is refused.
   *
   * Not tidiness — a step number is half the key on every draft it has written.
   * Deleting step 3 and adding another is prevented from reusing the number
   * (see `nextStep`), but the deletion itself would still strand the history:
   * the campaign would show two touches while sixty people had had three, and
   * `progressFor` would read "3 of 2".
   */
  async function deleteTouch(t: DripTouch) {
    if (everSent(t.campaign_id).has(t.step)) {
      return setError(
        `Touch ${t.step} has already gone out to somebody, so it cannot be deleted — ` +
          `the record of what they were sent hangs off it. Edit its wording, or end the campaign and start another.`,
      )
    }
    setBusy(`del-${t.id}`)
    setError(null)
    const { error } = await supabase.from('drip_touches').delete().eq('id', t.id)
    setBusy(null)
    if (error) return setError(error.message)
    await load()
  }

  /**
   * Put the mailing list into a campaign.
   *
   * Snapshot, not a live filter: these people are in it from now on, and a
   * change to the filters afterwards does not add or remove anybody. Decided
   * with Chris — a live audience means "who is in this campaign?" has no stable
   * answer, and the number she approved is not the number that gets emailed.
   *
   * `started_on` is today for everyone enrolled in one go. For the new-agent
   * campaign that is the day she noticed them, which is as close to their join
   * date as this system can honestly get until Metabase provides one.
   */
  async function enrolSelection(campaign: DripCampaign) {
    const ids = [...selection]
    if (ids.length === 0) return
    /**
     * Refused on a campaign that is not running.
     *
     * The button is identical on every card and the only thing telling them
     * apart is the name above it. Adding forty people to last year's finished
     * welcome sequence reported "40 people added", moved the count to 40, and
     * then did nothing for ever — and deleting that campaign later would cascade
     * them away without a single touch having been due.
     */
    if (campaign.status !== 'active') {
      setNoteFor(campaign.id)
      setNote(null)
      return setError(
        `"${campaign.name}" is ${campaign.status}, so nobody added to it would receive anything. ${campaign.status === 'paused' ? 'Resume it first.' : 'Reopen it first, or add them to a running campaign.'}`,
      )
    }
    setBusy(`enrol-${campaign.id}`)
    setError(null)
    const rows = ids.map((agent_id) => ({
      campaign_id: campaign.id,
      agent_id,
      started_on: today,
      status: 'active' as const,
    }))
    // Somebody already in the campaign is left exactly as they are — their day
    // zero and their history stay put. Re-enrolling them would restart the
    // sequence, which is never what ticking a name a second time means.
    const { data, error } = await supabase
      .from('drip_enrolments')
      .upsert(rows, { onConflict: 'campaign_id,agent_id', ignoreDuplicates: true })
      .select('id')
    setBusy(null)
    if (error) return setError(error.message)
    const added = (data ?? []).length
    setNoteFor(campaign.id)
    setNote(
      added === ids.length
        ? `${added} ${added === 1 ? 'person' : 'people'} added.`
        : `${added} added. ${ids.length - added} ${ids.length - added === 1 ? 'was' : 'were'} already in it and ${ids.length - added === 1 ? 'was' : 'were'} left where they are.`,
    )
    clearSelection()
    await load()
  }

  /**
   * Take one person out of a campaign.
   *
   * It asks first, and that is not politeness. This button sits inline beside
   * every name, it is one click, and it ends that person's sequence — they get
   * no further touches, ever. Every other destructive control here asks:
   * Delete this batch, Clear all, deleting a campaign. This one did not, and
   * the first real use of the feature ended with an enrolment stopped 68
   * seconds after it was created.
   */
  async function stopAgent(e: DripEnrolment, campaign: DripCampaign) {
    setBusy(`stop-${e.id}`)
    setError(null)
    const { data, error } = await supabase
      .from('drip_enrolments')
      .update({ status: 'stopped', stopped_reason: 'Taken out by hand', stopped_at: new Date().toISOString() })
      .eq('id', e.id)
      .select('id')
    setBusy(null)
    setConfirmStop(null)
    if (error) return setError(error.message)
    if (!Array.isArray(data) || data.length === 0) {
      return setError('That person is no longer in the campaign — it may have changed in another window. Reload.')
    }
    setNoteFor(campaign.id)
    setNote('Taken out. They will get no further touches — use "Put back in" if that was not what you meant.')
    await load()
  }

  /**
   * Undo a stop, putting somebody back where they left off.
   *
   * Their `started_on` never changed, so the schedule they were on is the
   * schedule they return to — and every touch they already had is still
   * recorded, so nothing is re-sent. Without this the only route back from a
   * misclick was editing the database by hand, which is not a route.
   */
  async function resumeAgent(e: DripEnrolment, campaign: DripCampaign) {
    if (campaign.status !== 'active') {
      return setError(
        `"${campaign.name}" is ${campaign.status}, so putting somebody back in would not start anything. Resume the campaign first.`,
      )
    }
    const touches = touchesOf(campaign.id)
    const startedOn = resumeStart(e.started_on, touches, sentSteps(campaign.id, e.agent_id), today)
    setBusy(`resume-${e.id}`)
    setError(null)
    const { data, error } = await supabase
      .from('drip_enrolments')
      .update({ status: 'active', stopped_reason: null, stopped_at: null, started_on: startedOn })
      .eq('id', e.id)
      .select('id')
    setBusy(null)
    if (error) return setError(error.message)
    if (!Array.isArray(data) || data.length === 0) {
      return setError('That person is no longer in the campaign — reload and look again.')
    }
    setNoteFor(campaign.id)
    setNote(
      startedOn === e.started_on
        ? 'Back in, on the dates they were already on. Touches they have already had are not sent again.'
        : 'Back in, picking up from today — their remaining touches run at the usual spacing from now, rather than all arriving at once because they were out for a while. Nothing they have already had is sent again.',
    )
    await load()
  }

  /**
   * Write the touch off for these people without emailing them.
   *
   * Whether a touch has happened is derived from the existence of a draft row,
   * so a batch that cannot be written stays due for ever. A campaign of twelve
   * who all later got tagged `no-outreach` greeted her with "Touch 2 is due for
   * 12 people" every single time she opened the tab, and every press of Approve
   * gave the same refusal. The only exits were pausing the whole campaign or
   * stopping all twelve by hand.
   *
   * These rows are `skipped`, which is a status the Outbox already understands
   * and already hides. It is an honest record: this touch was considered for
   * this person on this day and deliberately not sent.
   */
  async function skipTouch(campaign: DripCampaign, step: number, agentIds: string[]) {
    setBusy(`skip-${campaign.id}-${step}`)
    setError(null)
    const rows = agentIds.map((agent_id) => ({
      agent_id,
      trigger: dripTrigger(campaign.id),
      occurrence: stepOccurrence(step),
      subject: '(skipped)',
      body: '(skipped)',
      why: `${campaign.name} — touch ${step}`,
      label: `${campaign.name} — touch ${step}`,
      facts: { lane: 'reviewed', skipped_reason: 'nobody in the batch could be emailed' },
      status: 'skipped' as const,
    }))
    const { error } = await supabase
      .from('outreach_drafts')
      .upsert(rows, { onConflict: 'agent_id,trigger,occurrence', ignoreDuplicates: true })
    setBusy(null)
    if (error) return setError(error.message)
    setNoteFor(campaign.id)
    setNote(`Touch ${step} marked as skipped for ${agentIds.length}. Nobody was emailed.`)
    await load()
  }

  /**
   * Delete a finished campaign, its touches and its enrolments.
   *
   * FINISHED ONLY, and that is a real guard rather than tidiness: an active
   * campaign has people mid-sequence, and removing it would leave them halfway
   * through something that no longer exists with nothing on any screen to say
   * why the rest never came. Ending it first is a deliberate act with its own
   * button.
   *
   * WHAT SURVIVES, and this is the part worth being exact about:
   *
   *   - **Emails already in the Outbox stay.** They are real queued emails she
   *     was about to send; deleting a campaign must not quietly delete work she
   *     has not finished. She deletes those from the Outbox herself if she
   *     wants them gone, which is a separate and visible act.
   *   - **The record of what was sent stays**, on each agent's card. The drafts
   *     carry `drip:<id>` and nothing reads the campaign row to display them —
   *     `campaignIdOf` matches the prefix — so history does not develop a hole
   *     the moment somebody tidies up.
   *
   * What goes is the campaign row, its touches and its enrolments, by cascade.
   * That is a one-way door, so it asks first and says the numbers out loud.
   */
  async function deleteCampaign(campaign: DripCampaign) {
    if (campaign.status !== 'finished') {
      return setError('End the campaign first — a campaign with people still moving through it cannot be deleted.')
    }
    setBusy(`delete-${campaign.id}`)
    setError(null)
    const { data, error } = await supabase
      .from('drip_campaigns')
      .delete()
      .eq('id', campaign.id)
      .eq('status', 'finished')
      .select('id')
    setBusy(null)
    if (error) {
      setConfirmDelete(null)
      return setError(error.message)
    }
    // A delete that matched nothing comes back successful and empty. Without
    // this the campaign would vanish from the screen and still be in the
    // database — or, worse, look deleted to her and not to Renee. Reloaded on
    // this path too: whatever the screen is showing is out of date, and that is
    // the thing the message is telling her.
    if (!Array.isArray(data) || data.length === 0) {
      await load()
      return setError(
        'Nothing was deleted — the campaign may have been changed in another window. Reload and look again.',
      )
    }
    setNoteFor(null)
    setNote(`"${campaign.name}" deleted. Anything already in the Outbox is untouched, and the record of what was sent stays on each agent's card.`)
    await load()
  }

  async function setStatus(campaign: DripCampaign, status: DripCampaign['status']) {
    setBusy(`status-${campaign.id}`)
    setError(null)
    // "40 people added." sitting under a pill that now reads FINISHED is a
    // message describing a world that has moved on.
    setNote(null)
    // Row count, like every other write on this screen. A campaign deleted in
    // the other window otherwise reports a clean success and the pill changes.
    const { data, error } = await supabase
      .from('drip_campaigns')
      .update({ status })
      .eq('id', campaign.id)
      .select('id')
    setBusy(null)
    if (error) return setError(error.message)
    if (!data || data.length === 0) {
      setError(
        `"${campaign.name}" was not there to change — it may have been deleted in another window.`,
      )
      return void load()
    }
    await load()
  }

  /**
   * Approve one due touch: write the drafts and let her read them.
   *
   * The audience is resolved through the same `resolveAudience` the composer
   * uses, so a drip cannot reach somebody the composer would have refused —
   * no address, departed, or tagged out of this lane. Suppression is not
   * reimplemented here; it is the one rule in the system with two independent
   * enforcers already, and a third copy would be the one that drifts.
   */
  async function approve(campaign: DripCampaign, step: number, agentIds: string[]) {
    const touch = touchesOf(campaign.id).find((t) => t.step === step)
    if (!touch) return
    if (!touch.subject.trim() || !touch.body.trim()) {
      return setError(`Touch ${step} of "${campaign.name}" has no subject or no message yet.`)
    }
    if (tagsError || !tagsLoaded) {
      return setError('The tags did not load, so who should be left out is unknown. Nothing was queued. Reload the page.')
    }

    /**
     * The blast lane is refused HERE, not merely un-buttoned.
     *
     * The Approve button is not rendered for a blast campaign, so this is
     * unreachable today. It is one UI edit from being reachable, and the
     * consequence of getting there is resolving a blast audience under the
     * reviewed lane's suppression — which emails every agent tagged
     * `no-bulk-email` precisely because they asked not to be.
     */
    if (campaign.lane !== 'reviewed') {
      return setError('Group sending is not switched on yet — this needs the Resend upgrade.')
    }

    const picked = (agents ?? []).filter((a) => agentIds.includes(a.rezen_id))
    const audience = resolveAudience(picked, campaign.lane, tagsByAgent, touch.body, touch.subject)
    if (audience.recipients.length === 0) {
      return setError(
        'Nobody in that batch can be emailed — they have no address, have left, or are tagged out of this kind of email.',
      )
    }

    /**
     * The same refusal the composer makes, and it was missing here.
     *
     * `problemsWith` is what raises "Nothing will fill in {{firstname}} — it is
     * not a field on the agent record" and disables Send. Without it, a
     * mistyped fill-in in a touch is written into forty cards verbatim and only
     * a person reading every one of them would catch it. `resolveAudience` does
     * not cover this: its `missing-merge` only fires for fields that really
     * exist and happen to be blank, so a name that is not a field at all sails
     * through. The Guide promises this check by name.
     */
    const problems = problemsWith({ lane: campaign.lane, subject: touch.subject, body: touch.body, audience })
    if (!canSend(problems)) {
      const stops = problems.filter((p) => p.severity === 'stop').map((p) => p.message)
      return setError(
        `Touch ${step} cannot go out as written. ${stops.join(' ')} Open the campaign and fix the wording.`,
      )
    }

    setBusy(`approve-${campaign.id}-${step}`)
    setError(null)
    const r = await queueReviewed({
      audience,
      subject: touch.subject,
      body: touch.body,
      label: `${campaign.name} — touch ${step}`,
      at: new Date(),
      trigger: dripTrigger(campaign.id),
      occurrence: stepOccurrence(step),
    })
    setBusy(null)
    if (r.error) return setError(r.error)
    setNoteFor(campaign.id)
    setNote(
      `${r.created} ${r.created === 1 ? 'email is' : 'emails are'} waiting in the Outbox for you to read and send.` +
        (r.alreadyQueued ? ` ${r.alreadyQueued} had already been written.` : ''),
    )
    await load()
  }

  const usableTemplates = templates
    .filter((t) => !isGuidanceTemplate(t.key))
    .filter((t) => t.subject.trim() !== '' || t.body.trim() !== '')

  /**
   * Every campaign the morning update will actually put new agents into.
   *
   * There is no database rule stopping there being two, and the Pages Function
   * enrols into ALL of them — so a second one is not a duplicate that gets
   * ignored, it is every new agent starting two welcome sequences at once and
   * receiving both. Nothing about the two cards on screen would explain it.
   * Warned about rather than blocked, because replacing one with another is a
   * real thing to want and the honest fix is to pause the old one.
   */
  const listeningCampaigns = bundle.campaigns.filter(isListeningForNewAgents)

  return (
    <div className="board tpl">
      <section className="sec">
        <p className="hint">
          A campaign is a sequence of emails — the first one now, the next after however many days
          you choose, and so on. <strong>Nothing here sends anything.</strong> When a touch comes
          due it is offered below, and approving it puts the emails in the <strong>Outbox</strong>,
          where you read and send them one at a time.
        </p>
        {bundle.warning && <p className="error">{bundle.warning}</p>}
        {/*
          Two live New Agent campaigns is not a duplicate that gets ignored —
          the morning update enrols into every one of them, so each new agent
          starts both sequences and receives both. Said at the top of the page
          rather than on either card, because neither card is the wrong one.
        */}
        {listeningCampaigns.length > 1 && (
          <p className="error">
            {listeningCampaigns.length} campaigns are picking up new agents automatically
            ({listeningCampaigns.map((c) => `"${c.name}"`).join(', ')}). Every new agent will be put
            in all of them and will get all of their emails. Pause the ones you do not want.
          </p>
        )}
        {error && <p className="error">{error}</p>}
        {note && noteFor === null && <p className="cnt">{note}</p>}
      </section>

      <section className="sec">
        <h3>New campaign</h3>
        {/*
          `.addtag` is a flex row with no wrap and `input { flex: 1 }`, built
          for two children. This form now has up to five, and a date input has a
          wide minimum, so without wrapping the name box is squeezed to nothing
          and the row overflows the card. Set here rather than in the shared
          class, which several other screens rely on.
        */}
        <div className="addtag" style={{ flexWrap: 'wrap' }}>
          <input
            value={newName}
            placeholder="e.g. New agent welcome"
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => {
              // `repeat` guards a held-down Enter, which otherwise creates a
              // campaign per keyboard repeat — about 25 a second.
              if (e.key === 'Enter' && !e.repeat) void createCampaign()
            }}
          />
          <select value={newLane} onChange={(e) => setNewLane(e.target.value as DripLane)}>
            <option value="reviewed">{LANE_LABEL.reviewed}</option>
            <option value="blast">{LANE_LABEL.blast}</option>
          </select>
          <select
            value={newEnrolment}
            onChange={(e) => setNewEnrolment(e.target.value as DripEnrolmentMode)}
          >
            <option value="manual">{ENROLMENT_LABEL.manual}</option>
            <option value="trigger">{ENROLMENT_LABEL.trigger}</option>
          </select>
          {newEnrolment === 'trigger' && (
            <input
              type="date"
              aria-label="Only agents who join on or after this date"
              /*
                `width: auto` as well as the flex, because `.tpl input` sets
                `width: 100%` and a `flex-basis` of `auto` hands the sizing back
                to it — which would lay the date box across a whole row of its
                own. The name box escapes that only because `flex: 1` gives it a
                basis of zero.
              */
              style={{ flex: '0 0 auto', width: 'auto' }}
              value={newListens}
              onChange={(e) => setNewListensFrom(e.target.value)}
            />
          )}
          <button
            className="btn sm"
            disabled={
              !newName.trim() ||
              busy === 'new' ||
              (newEnrolment === 'trigger' && !isCalendarDate(newListens))
            }
            onClick={() => void createCampaign()}
          >
            {busy === 'new' ? 'Creating…' : 'Create'}
          </button>
        </div>
        {newEnrolment === 'trigger' && (
          <p className="hint">
            {/*
              This used to say that without a date the campaign "would welcome
              everybody who joined in the last three months". That is what the
              date is FOR, but it is not what happens — the Function skips a
              campaign with no date entirely — and stated as fact it reads as an
              argument for setting the date as far back as possible, which is
              the one setting that causes real harm.
            */}
            <strong>Only agents who join on or after this date.</strong> The morning update looks
            at the last 90 days of joiners, so the date is what stops a brand-new campaign
            welcoming people who were welcomed months ago. <strong>Today is the right answer
            almost always.</strong>
          </p>
        )}
        {newEnrolment === 'trigger' && !isCalendarDate(newListens) && (
          <p className="error">
            <strong>That start date is not a day on the calendar.</strong> Pick the date again —
            the campaign cannot be created until it is a real day.
          </p>
        )}
        {newEnrolment === 'trigger' && isCalendarDate(newListens) && newListens > today && (
          <p className="hint">
            <strong>
              That date is in the future, so nobody will be added until {formatDateLong(newListens)}.
            </strong>
          </p>
        )}
        {newEnrolment === 'trigger' && isCalendarDate(newListens) && newListens < today && (
          /*
            The dangerous direction, and the one nothing warned about.

            An agent picked up by the morning update is anchored to the day they
            joined the brokerage, not the day they were added — the Function
            sends `started_on: joinDate`. So a start date far enough back gives
            somebody a schedule that began weeks ago, and every touch whose day
            has passed is due at once. Approving one reloads the screen and
            offers the next: three "welcome" emails to one person in three
            clicks, each looking like an ordinary due touch.
          */
          <p className="error">
            <strong>
              That date is {daysBetween(newListens, today)}{' '}
              {daysBetween(newListens, today) === 1 ? 'day' : 'days'} ago, and people are counted
              from the day they joined — not the day they are added.
            </strong>{' '}
            Somebody who joined back then starts that far into the sequence, so every touch whose
            day has already passed comes due for them at once. The further back this date is, the
            more of the sequence arrives in one morning.
          </p>
        )}
        {newEnrolment === 'trigger' && listeningCampaigns.length > 0 && (
          <p className="error">
            <strong>
              {listeningCampaigns.length === 1
                ? `"${listeningCampaigns[0].name}" is already picking up new agents automatically.`
                : `${listeningCampaigns.length} campaigns are already picking up new agents automatically (${listeningCampaigns.map((c) => `"${c.name}"`).join(', ')}).`}
            </strong>{' '}
            Another one does not replace them — every new agent would be put in all of them and
            would get all of their emails. Pause the others first if this is meant to take over.
          </p>
        )}
        <p className="hint">
          <strong>Read and send each one</strong> puts a card per agent in your Outbox and goes out
          through your Real Brokerage Gmail — names can be filled in.{' '}
          <strong>Same message to everybody</strong> goes out in one go through Resend and cannot
          use names. Group sending is not switched on yet: it needs the Resend upgrade, so those
          campaigns can be built and filled but not approved.
        </p>
      </section>

      {bundle.campaigns.length === 0 && (
        <section className="sec">
          <p className="hint">No campaigns yet.</p>
        </section>
      )}

      {bundle.campaigns.map((c) => {
        const touches = touchesOf(c.id)
        const enrolments = enrolmentsOf(c.id)
        const activeCount = enrolments.filter((e) => e.status === 'active').length
        /**
         * Counted, not inferred as total minus active. `finished` is a real
         * enrolment status in the schema, so "everybody else is stopped" would
         * one day describe people who had simply completed — and then offer to
         * put them back in, which is not a button that exists for them.
         */
        const stoppedCount = enrolments.filter((e) => e.status === 'stopped').length
        /** Touches that exist but cannot be sent, because nothing is written in them. */
        const blankTouches = touches.filter((t) => !t.subject.trim() || !t.body.trim())
        const sentByAgent = new Map(enrolments.map((e) => [e.agent_id, sentSteps(c.id, e.agent_id)]))
        const complete = isComplete(touches, enrolments, sentByAgent)
        const due = dueTouches(c, touches, enrolments, sentFor(c.id), today)
        const isOpen = open === c.id
        /** The listening date as edited, falling back to the stored one. */
        const listensValue = listensDraft[c.id] ?? c.listens_from ?? ''
        /** Is the morning update actually putting people into this one? */
        const listening = isListeningForNewAgents(c)
        /** Listening, but not yet — the start date has not arrived. */
        const notYet = listening && !!c.listens_from && c.listens_from > today
        const allStopped = stoppedCount > 0 && stoppedCount === enrolments.length

        return (
          <section className="sec" key={c.id}>
            <h3>
              {c.name}
              <span className={`pill ${c.status === 'active' ? 's-drafted' : 's-skipped'}`}>
                {complete && c.status === 'active' ? 'all touches done' : c.status}
              </span>
            </h3>
            <p className="hint">
              {LANE_LABEL[c.lane]} · {enrolDescription(c)} ·{' '}
              {touches.length} {touches.length === 1 ? 'touch' : 'touches'} ·{' '}
              {/*
                The count is of PEOPLE IN THE CAMPAIGN, with the stopped ones
                said separately. It used to print the active count alone, so a
                campaign holding one stopped person read "0 people (1 stopped)"
                — which parses as empty at a glance, and sent us looking for a
                bug in enrolment when the truth was that somebody had been taken
                out by hand.
              */}
              {enrolments.length} {enrolments.length === 1 ? 'person' : 'people'}
              {enrolments.length !== activeCount &&
                ` · ${stoppedCount} stopped, ${activeCount} still going`}
            </p>
            {note && noteFor === c.id && <p className="cnt">{note}</p>}
            {/*
              A campaign that will never do anything says so.

              ACTIVE, with touches, and nobody in it looks armed and ready. It
              is not — a touch only comes due for a person. This is how somebody
              discovers in three months that the welcome sequence was never
              running, so it is stated on the card rather than left to be
              inferred from a zero.
            */}
            {c.status === 'active' && touches.length > 0 && activeCount === 0 && (
              /*
                An empty TRIGGER campaign is not the same state as an empty
                manual one, and saying "nobody is in this campaign, so nothing
                will come due" about one is alarming and wrong. It is armed and
                waiting; the honest sentence is that nobody has joined yet.
              */
              <p className="hint">
                <strong>
                  {/*
                    "Nobody has JOINED yet" was a claim this screen cannot
                    support. The daily run only offers agents it has just
                    created, and skips any joiner whose date it could not read —
                    so people can have joined and still not be here. What it
                    knows is that nobody has been added.
                  */}
                  {listening && enrolments.length === 0
                    ? 'Nobody has been added yet, so nothing is due.'
                    : allStopped
                      ? 'Everybody in this campaign has been stopped, so nothing is due for them.'
                      : enrolments.length === 0
                        ? 'Nobody is in this campaign, so nothing will come due.'
                        : 'Nobody in this campaign is still going, so nothing is due for them.'}
                </strong>{' '}
                {listening
                  ? (notYet
                      ? `It starts picking up new agents on ${formatDateLong(c.listens_from)}.`
                      : 'The next new agent the morning update finds will be added on their own.') +
                    (allStopped ? ' Open it to put the stopped ones back in.' : '')
                  : allStopped
                    ? 'Open it and put somebody back in to start it again.'
                    : 'Tick people on any agent tab, then use the button below.'}
              </p>
            )}
            {/*
              The state the card's own instructions lead you into.

              "Add a touch" writes an empty subject and body, so the moment she
              presses it the no-touches warning disappears and nothing replaces
              it. The card then reads "1 touch · 40 people" with a live
              "Approve 40" button — fully armed, and refused the moment she
              presses it. This is the likeliest dead campaign in the feature and
              it was the one state not covered.
            */}
            {c.status === 'active' && blankTouches.length > 0 && (
              <p className="hint">
                <strong>
                  {blankTouches.length === touches.length
                    ? `Nothing is written yet${touches.length === 1 ? '' : ' in any touch'}, so nothing can go out.`
                    : `Touch ${blankTouches.map((t) => t.step).join(' and ')} ${blankTouches.length === 1 ? 'has' : 'have'} no subject or no message yet.`}
                </strong>{' '}
                Open it and finish the wording.
              </p>
            )}
            {c.status === 'active' && touches.length === 0 && (
              <p className="hint">
                <strong>This campaign has no touches yet, so nothing will come due.</strong> Open it
                and add the first one.
              </p>
            )}

            {/* Due work first — it is the only thing on this screen with a deadline. */}
            {due.map((d) => {
              /**
               * The number on the button is the number that will be written.
               *
               * It used to be the raw enrolment count, so "Approve 40" was
               * followed by "31 emails are waiting" with no explanation of the
               * nine. The composer says the split before the button rather than
               * after it, and so should this.
               */
              const pickedForStep = (agents ?? []).filter((a) =>
                d.agents.some((x) => x.agentId === a.rezen_id),
              )
              const touch = touches.find((t) => t.step === d.step)
              const reach = touch
                ? resolveAudience(pickedForStep, 'reviewed', tagsByAgent, touch.body, touch.subject)
                    .recipients.length
                : 0
              const excluded = d.agents.length - reach
              const oldest = Math.max(...d.agents.map((a) => a.overdueDays))

              return (
                <div className="row" key={`${d.campaignId}-${d.step}`}>
                  <span className="k">
                    <strong>Touch {d.step}</strong> is due for {d.agents.length}{' '}
                    {d.agents.length === 1 ? 'person' : 'people'}
                    {oldest > 0 && <span className="hint"> · oldest was due {oldest} days ago</span>}
                    {excluded > 0 && (
                      <span className="hint">
                        {' '}
                        · {excluded} cannot be emailed (no address, left, or tagged out)
                      </span>
                    )}
                  </span>
                  <span className="v">
                    {c.lane === 'blast' ? (
                      <span className="hint">Group sending is not switched on yet</span>
                    ) : reach === 0 ? (
                      <button
                        className="btn ghost sm"
                        disabled={busy === `skip-${c.id}-${d.step}`}
                        onClick={() => void skipTouch(c, d.step, d.agents.map((a) => a.agentId))}
                      >
                        Nobody can be emailed — skip it
                      </button>
                    ) : (
                      <button
                        className="btn sm"
                        disabled={busy === `approve-${c.id}-${d.step}`}
                        onClick={() => void approve(c, d.step, d.agents.map((a) => a.agentId))}
                      >
                        {busy === `approve-${c.id}-${d.step}` ? 'Writing…' : `Approve ${reach}`}
                      </button>
                    )}
                  </span>
                </div>
              )
            })}

            <div className="savebar">
              <button className="btn ghost sm" onClick={() => setOpen(isOpen ? null : c.id)}>
                {isOpen ? 'Close' : 'Open'}
              </button>
              <button
                className="btn ghost sm"
                disabled={selection.size === 0 || busy === `enrol-${c.id}`}
                onClick={() => void enrolSelection(c)}
              >
                {selection.size === 0 ? 'Tick people to add them' : `Add the ${selection.size} on your list`}
              </button>
              {c.status === 'active' ? (
                <button className="btn ghost sm" onClick={() => void setStatus(c, 'paused')}>Pause</button>
              ) : c.status === 'paused' ? (
                <button className="btn ghost sm" onClick={() => void setStatus(c, 'active')}>Resume</button>
              ) : null}
              {/*
                Resuming or reopening a New Agent campaign arms the listener
                again — with whatever date it was paused holding. Pause one in
                March and reopen it in August and it starts picking up everybody
                the morning update creates who joined since March, each anchored
                to their own join date. That is the same hazard the date box
                warns about, reached by a button that said nothing.
              */}
              {c.status !== 'active' &&
                c.enrolment === 'trigger' &&
                c.trigger_key === NEW_AGENT_TRIGGER &&
                !!c.listens_from &&
                c.listens_from < today && (
                  <span className="hint">
                    Starting this again picks up new agents from {formatDateLong(c.listens_from)} —{' '}
                    {daysBetween(c.listens_from, today)}{' '}
                    {daysBetween(c.listens_from, today) === 1 ? 'day' : 'days'} ago. Open it and
                    move the date on first if you do not want their earlier touches all at once.
                  </span>
                )}
              {c.status !== 'finished' && (
                <button className="btn ghost sm" onClick={() => void setStatus(c, 'finished')}>End it</button>
              )}
              {/*
                Ending a campaign was a one-way door with Delete as the only
                thing on the other side of it. Nothing about "End it" warns you
                of that, and it sits next to Pause, which is entirely reversible.
              */}
              {c.status === 'finished' && (
                <button className="btn ghost sm" onClick={() => void setStatus(c, 'active')}>
                  Reopen
                </button>
              )}
              {/*
                Delete appears only once a campaign is finished, and asks before
                it acts. Inline rather than a browser confirm, which is easy to
                dismiss without reading and impossible to word properly.
              */}
              {c.status === 'finished' &&
                (confirmDelete === c.id ? (
                  <>
                    <span className="hint">
                      Delete "{c.name}" and its {touches.length}{' '}
                      {touches.length === 1 ? 'touch' : 'touches'}
                      {enrolments.length > 0 && ` and ${enrolments.length} enrolment${enrolments.length === 1 ? '' : 's'}`}?
                      {pendingFor(c.id) > 0 &&
                        ` ${pendingFor(c.id)} email${pendingFor(c.id) === 1 ? '' : 's'} already waiting in the Outbox will stay there.`}
                    </span>
                    <button
                      className="btn sm"
                      disabled={busy === `delete-${c.id}`}
                      onClick={() => void deleteCampaign(c)}
                    >
                      {busy === `delete-${c.id}` ? 'Deleting…' : 'Yes, delete it'}
                    </button>
                    <button className="btn ghost sm" onClick={() => setConfirmDelete(null)}>
                      Keep it
                    </button>
                  </>
                ) : (
                  <button className="btn ghost sm" onClick={() => setConfirmDelete(c.id)}>
                    Delete
                  </button>
                ))}
            </div>

            {isOpen && (
              <>
                {c.enrolment === 'trigger' && (
                  <>
                    <h3>Who it picks up</h3>
                    <div className="row">
                      <span className="k">New agents who join on or after</span>
                      <span className="v">
                        <input
                          type="date"
                          aria-label={`${c.name} — only agents who join on or after this date`}
                          style={{ width: 'auto' }}
                          value={listensValue}
                          onChange={(e) =>
                            setListensDraft((d) => ({ ...d, [c.id]: e.target.value }))
                          }
                        />{' '}
                        <button
                          className="btn ghost sm"
                          disabled={
                            busy === `listens-${c.id}` ||
                            !listensValue ||
                            listensValue === (c.listens_from ?? '')
                          }
                          onClick={() => void saveListensFrom(c, listensValue)}
                        >
                          {busy === `listens-${c.id}` ? 'Saving…' : 'Save'}
                        </button>
                      </span>
                    </div>
                    <p className="hint">
                      {/*
                        Said before she changes it, not after. The daily run only
                        looks at agents it has just created, so an earlier date
                        does not reach back into the roster — which is exactly
                        what "start from last Monday instead" sounds like it
                        should do.
                      */}
                      Changing this only affects agents the morning update finds from now on. It
                      does not go back and add anybody who is already on the roster, and it does not
                      take anybody out of the campaign.
                      {c.status !== 'active' && (
                        <>
                          {' '}
                          <strong>
                            This campaign is {c.status}, so nobody is being picked up at the moment.
                          </strong>
                        </>
                      )}
                    </p>
                    {/*
                      Only about a change she is ABOUT to make. An existing
                      campaign's date is nearly always in the past — that is
                      what a campaign that has been running looks like — so
                      warning on the stored value would paint every healthy card
                      red and the warning would be worth nothing on the day it
                      mattered.
                    */}
                    {listensDraft[c.id] !== undefined &&
                      listensValue !== (c.listens_from ?? '') &&
                      isCalendarDate(listensValue) &&
                      listensValue < today && (
                      <p className="error">
                        <strong>
                          {formatDateLong(listensValue)} is {daysBetween(listensValue, today)}{' '}
                          {daysBetween(listensValue, today) === 1 ? 'day' : 'days'} ago, and new
                          agents are counted from the day they joined — not the day they are added.
                        </strong>{' '}
                        Somebody who joined back then starts that far into the sequence, so every
                        touch whose day has already passed comes due for them at once.
                      </p>
                    )}
                    {/*
                      The other direction, which the create form warns about and
                      this panel did not. A date typed into the future stops
                      enrolment dead until it arrives, and on a campaign that
                      already has people in it nothing else on the card says so.
                    */}
                    {listensDraft[c.id] !== undefined &&
                      listensValue !== (c.listens_from ?? '') &&
                      isCalendarDate(listensValue) &&
                      listensValue > today && (
                      <p className="error">
                        <strong>
                          {formatDateLong(listensValue)} is in the future, so nobody new will be
                          added until then.
                        </strong>
                      </p>
                    )}
                  </>
                )}
                <h3>Touches</h3>
                {touches.length === 0 && <p className="hint">No touches yet. The first one is day zero.</p>}
                {touches.map((t, i) => (
                  <TouchEditor
                    key={t.id}
                    touch={t}
                    previousStep={i === 0 ? null : touches[i - 1].step}
                    templates={usableTemplates}
                    busy={busy === `save-${t.id}` || busy === `del-${t.id}`}
                    onSave={(patch) => saveTouch(t, patch)}
                    onDelete={() => deleteTouch(t)}
                  />
                ))}
                <button
                  className="btn sm"
                  disabled={busy === `touch-${c.id}`}
                  onClick={() => void addTouch(c)}
                >
                  Add a touch
                </button>

                <h3>Who is in it</h3>
                {enrolments.length === 0 && (
                  <p className="hint">
                    {c.enrolment !== 'trigger' ? (
                      <>
                        Nobody yet. Tick people on any agent tab, then come back and use the button
                        above.
                      </>
                    ) : listening ? (
                      <>
                        Nobody yet. New agents who join on or after{' '}
                        {c.listens_from ? formatDateLong(c.listens_from) : 'the start date'} are put
                        in by the morning update. You can also tick people on any agent tab and use
                        the button above — though somebody added by hand is counted from today,
                        while somebody the update finds is counted from the day they joined.
                      </>
                    ) : (
                      <>
                        {/*
                          `listening` is false for two different reasons — the
                          campaign is paused or finished, or it has no start
                          date at all. Saying "while this campaign is active"
                          about the second one is nonsense, and the second one
                          is the case somebody has to act on.
                        */}
                        Nobody yet, and nobody is being picked up:{' '}
                        {c.status !== 'active'
                          ? `this campaign is ${c.status}.`
                          : 'it has no start date, so the morning update skips it. Set one above.'}{' '}
                        You can still tick people on any agent tab and use the button above.
                      </>
                    )}
                  </p>
                )}
                {enrolments.map((e) => {
                  const p = progressFor(e, touches, sentByAgent.get(e.agent_id) ?? new Set())
                  const who = byId.get(e.agent_id)
                  return (
                    <div className="row" key={e.id}>
                      <span className="k">{who ? fullName(who) : e.agent_id}</span>
                      <span className="v">
                        {e.status === 'active' ? (
                          <>
                            <span className="hint">
                              touch {Math.min(p.done + 1, p.total)} of {p.total}
                              {p.nextDue ? ` · next ${p.nextDue}` : ' · nothing left'}
                            </span>{' '}
                            {confirmStop === e.id ? (
                              <>
                                <span className="hint">Take them out for good?</span>{' '}
                                <button
                                  className="btn sm"
                                  disabled={busy === `stop-${e.id}`}
                                  onClick={() => void stopAgent(e, c)}
                                >
                                  {busy === `stop-${e.id}` ? 'Stopping…' : 'Yes, stop them'}
                                </button>{' '}
                                <button className="btn ghost sm" onClick={() => setConfirmStop(null)}>
                                  Leave them in
                                </button>
                              </>
                            ) : (
                              <button className="btn ghost sm" onClick={() => setConfirmStop(e.id)}>
                                Stop
                              </button>
                            )}
                          </>
                        ) : (
                          <>
                            <span className="hint">
                              {e.status} after {p.done} of {p.total}
                              {e.stopped_reason ? ` — ${e.stopped_reason}` : ''}
                            </span>{' '}
                            {e.status === 'stopped' &&
                              (c.status === 'active' ? (
                                <button
                                  className="btn ghost sm"
                                  disabled={busy === `resume-${e.id}`}
                                  onClick={() => void resumeAgent(e, c)}
                                >
                                  {busy === `resume-${e.id}` ? 'Putting back…' : 'Put back in'}
                                </button>
                              ) : (
                                // Offered on a paused or finished campaign, this
                                // said "back on schedule" about somebody who
                                // could never receive anything.
                                <span className="hint">
                                  ({c.status === 'paused' ? 'resume' : 'reopen'} the campaign to put
                                  them back)
                                </span>
                              ))}
                          </>
                        )}
                      </span>
                    </div>
                  )
                })}

                {touches.length > 0 && enrolments.length > 0 && (
                  <p className="hint" style={{ marginTop: 8 }}>
                    Somebody starting today would get:{' '}
                    {scheduleFor(today, touches).map((s) => `touch ${s.step} on ${s.due}`).join(', ')}.
                    {listening && (
                      <>
                        {' '}
                        A new agent the morning update adds is counted from the day they joined the
                        brokerage, not from the day they were added, so their dates run earlier than
                        this.
                      </>
                    )}
                  </p>
                )}
              </>
            )}
          </section>
        )
      })}
    </div>
  )
}

/**
 * One touch, edited without the page reaching in and changing it underneath.
 *
 * THE BUG THIS SHAPE EXISTS TO PREVENT, twice over:
 *
 *   1. The fields were `defaultValue`, which React ignores after the first
 *      render. Picking a template wrote the wording to the database and the
 *      boxes went on showing the old empty text — so it looked as though the
 *      dropdown had done nothing. Clicking into the subject and out again then
 *      compared the stale empty box against the saved template subject, decided
 *      they differed, and wrote the empty string back over it. The template was
 *      destroyed by a click, silently, with no way to tell.
 *
 *   2. The delay was a controlled number input saving on every keystroke.
 *      Changing 30 to 45 goes 3, then "", then 4, then 45 — and "" coerced to
 *      **0**. A touch with a delay of nought falls on the same day as the one
 *      before it, so for the moment that value was stored, that touch was due
 *      today for everybody who had had the previous one. A two-keystroke edit,
 *      interrupted, is "email all 214 of them again this morning".
 *
 * So: local state seeded from the row, saved on blur, and the delay is refused
 * rather than coerced when it is blank. `seed` re-seeds only when the stored
 * value genuinely changes — picking a template updates the boxes, while a
 * reload triggered by something else leaves her typing alone.
 */
function TouchEditor({
  touch,
  previousStep,
  templates,
  busy,
  onSave,
  onDelete,
}: {
  touch: DripTouch
  previousStep: number | null
  templates: EmailTemplate[]
  busy: boolean
  onSave: (patch: Partial<DripTouch>) => void | Promise<void>
  onDelete: () => void | Promise<void>
}) {
  const [subject, setSubject] = useState(touch.subject)
  const [body, setBody] = useState(touch.body)
  const [delay, setDelay] = useState(String(touch.delay_days))
  const [delayError, setDelayError] = useState<string | null>(null)

  // Re-seed when the row itself changes — a template being picked, or another
  // window editing it — and not otherwise.
  useEffect(() => setSubject(touch.subject), [touch.subject])
  useEffect(() => setBody(touch.body), [touch.body])
  useEffect(() => setDelay(String(touch.delay_days)), [touch.delay_days])

  const saveDelay = () => {
    const n = Number(delay)
    if (delay.trim() === '' || !Number.isFinite(n) || n < 0 || !Number.isInteger(n)) {
      setDelayError('That needs to be a whole number of days.')
      setDelay(String(touch.delay_days))
      return
    }
    /**
     * Nought days after the previous touch means two emails on one day. The
     * database allows it; nothing anybody wants does.
     */
    if (n === 0) {
      setDelayError('A touch has to be at least one day after the one before it.')
      setDelay(String(touch.delay_days))
      return
    }
    setDelayError(null)
    if (n !== touch.delay_days) void onSave({ delay_days: n })
  }

  return (
    <div className="tpl touch">
      <div className="when">
        <span className="num">Touch {touch.step}</span>
        {previousStep === null ? (
          <span className="plain">— sent on that person's first day in the campaign</span>
        ) : (
          <>
            <span className="plain">—</span>
            <input
              type="number"
              min={1}
              value={delay}
              onChange={(e) => setDelay(e.target.value)}
              onBlur={saveDelay}
              aria-label={`Days after touch ${previousStep}`}
            />
            <span className="plain">days after touch {previousStep}</span>
          </>
        )}
      </div>
      {delayError && <p className="error">{delayError}</p>}

      <label>Start from a template</label>
      <select
        value={touch.template_key ?? ''}
        onChange={(e) => {
          const tpl = templates.find((x) => x.key === e.target.value)
          if (tpl) void onSave({ subject: tpl.subject, body: tpl.body, template_key: tpl.key })
        }}
      >
        <option value="">Write it myself…</option>
        {templates.map((tpl) => (
          <option key={tpl.key} value={tpl.key}>{tpl.name}</option>
        ))}
      </select>

      <label>Subject</label>
      <input
        value={subject}
        onChange={(e) => setSubject(e.target.value)}
        onBlur={() => {
          if (subject !== touch.subject) void onSave({ subject })
        }}
      />

      <label>Message</label>
      <textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        onBlur={() => {
          if (body !== touch.body) void onSave({ body })
        }}
      />

      <div className="savebar">
        <button className="btn ghost sm" disabled={busy} onClick={() => void onDelete()}>
          Delete touch {touch.step}
        </button>
        <span className="status">Saved when you click away.</span>
      </div>
    </div>
  )
}
